import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, PaperPlaneTilt, Check } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewForm({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: "",
    rating: 5,
    comment: "",
  });
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Por favor ingresa tu nombre");
      return;
    }

    if (!formData.comment.trim()) {
      toast.error("Por favor escribe tu comentario");
      return;
    }

    if (formData.comment.length < 10) {
      toast.error("El comentario debe tener al menos 10 caracteres");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/reviews`, formData);
      setSubmitted(true);
      toast.success("¡Gracias por tu reseña!");
      
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose();
        setSubmitted(false);
        setFormData({ name: "", rating: 5, comment: "" });
      }, 2000);
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Error al enviar la reseña. Intenta de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStarClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#E05A47] to-[#E8A753] p-6 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold">
              Deja tu reseña
            </h2>
            <p className="text-white/80 mt-1">
              Tu opinión nos ayuda a mejorar
            </p>
          </div>

          {/* Form */}
          {!submitted ? (
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="review-name" className="text-stone-700 font-medium">
                  Tu nombre
                </Label>
                <Input
                  id="review-name"
                  placeholder="¿Cómo te llamas?"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 rounded-xl border-stone-200 focus:border-[#E05A47] focus:ring-[#E05A47]"
                  data-testid="review-input-name"
                />
              </div>

              {/* Rating */}
              <div className="space-y-2">
                <Label className="text-stone-700 font-medium">
                  Calificación
                </Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleStarClick(star)}
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(0)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`review-star-${star}`}
                    >
                      <Star
                        weight={star <= (hoveredStar || formData.rating) ? "fill" : "regular"}
                        className={`w-8 h-8 transition-colors ${
                          star <= (hoveredStar || formData.rating)
                            ? "text-amber-400"
                            : "text-stone-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-stone-500">
                  {formData.rating === 5 && "¡Excelente!"}
                  {formData.rating === 4 && "Muy bueno"}
                  {formData.rating === 3 && "Bueno"}
                  {formData.rating === 2 && "Regular"}
                  {formData.rating === 1 && "Necesita mejorar"}
                </p>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label htmlFor="review-comment" className="text-stone-700 font-medium">
                  Tu experiencia
                </Label>
                <textarea
                  id="review-comment"
                  placeholder="Cuéntanos cómo fue tu estancia..."
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  className="w-full h-28 px-4 py-3 rounded-xl border border-stone-200 focus:border-[#E05A47] focus:ring-1 focus:ring-[#E05A47] focus:outline-none resize-none text-sm"
                  data-testid="review-input-comment"
                />
                <p className="text-xs text-stone-400">
                  {formData.comment.length}/500 caracteres
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-[#E05A47] hover:bg-[#C84938] text-white rounded-xl font-semibold shadow-lg shadow-[#E05A47]/25 disabled:opacity-50"
                data-testid="review-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <PaperPlaneTilt className="w-5 h-5" />
                    Enviar reseña
                  </span>
                )}
              </Button>

              <p className="text-center text-xs text-stone-400">
                Tu reseña se publicará inmediatamente
              </p>
            </form>
          ) : (
            /* Success State */
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", damping: 15 }}
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center"
              >
                <Check className="w-10 h-10 text-green-600" weight="bold" />
              </motion.div>
              <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-bold text-stone-800 mb-2">
                ¡Gracias por tu reseña!
              </h3>
              <p className="text-stone-600">
                La revisaremos pronto y aparecerá en el sitio.
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
